import { useEffect, useSyncExternalStore } from "react";

import { hasSupabasePublicConfig } from "../../Supabase/SupabaseConfig";

import { BEDCA_FOOD_CATALOG } from "./foodCatalog";
import { fetchSupabaseFoods } from "./supabaseFoodCatalog";

export const CATALOG_FOOD_QUERY = Object.freeze({
  schema: "api",
  view: "catalog_foods_search",
  columns: [
    "id",
    "name",
    "type",
    "source",
    "code",
    "external_source_id",
    "name_es",
    "name_en",
    "notes",
    "reference_quantity_g",
    "selected_measure_id",
    "common_measures",
    "nutrients_per_100g",
    "allergen_codes",
    "allergen_data_complete",
    "is_vegetarian",
    "is_vegan",
    "dietary_data_complete",
    "dietary_suitability",
    "brand",
    "store",
    "website",
    "origin",
    "schema_version",
    "created_at",
    "updated_at",
    "calories",
    "protein",
    "carbs",
    "fats",
    "fiber",
  ].join(","),
  orderBy: "name",
  limit: 1000,
});

function createCatalogFoodSnapshot({
  foods = [],
  hasLoaded = false,
  isLoading = false,
  error = null,
  source = "uninitialized",
} = {}) {
  return {
    foods: Array.isArray(foods) ? foods : [],
    hasLoaded,
    isLoading,
    error,
    source,
  };
}

export class CatalogFoodStore {
  constructor({
    fetchFoods = fetchSupabaseFoods,
    hasPublicConfig = hasSupabasePublicConfig,
    fallbackFoods = BEDCA_FOOD_CATALOG,
  } = {}) {
    this.fetchFoods = fetchFoods;
    this.hasPublicConfig = hasPublicConfig;
    this.fallbackFoods = Array.isArray(fallbackFoods) ? fallbackFoods : [];
    this.listeners = new Set();
    this.inFlightPromise = null;
    this.snapshot = createCatalogFoodSnapshot(
      this.hasPublicConfig()
        ? undefined
        : {
            foods: this.fallbackFoods,
            hasLoaded: true,
            source: "fallback",
          },
    );
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => {
    return this.snapshot;
  };

  getCachedFoods = () => {
    return this.snapshot.foods;
  };

  emitChange = () => {
    this.listeners.forEach((listener) => listener());
  };

  setSnapshot = (nextSnapshot) => {
    this.snapshot = createCatalogFoodSnapshot(nextSnapshot);
    this.emitChange();
  };

  ensureLoaded = async ({ force = false } = {}) => {
    if (!force && this.snapshot.hasLoaded) {
      return this.snapshot.foods;
    }

    if (!force && this.inFlightPromise) {
      return this.inFlightPromise;
    }

    if (!this.hasPublicConfig()) {
      this.setSnapshot({
        foods: this.fallbackFoods,
        hasLoaded: true,
        isLoading: false,
        error: null,
        source: "fallback",
      });
      return this.snapshot.foods;
    }

    this.setSnapshot({
      ...this.snapshot,
      isLoading: true,
      error: null,
    });

    this.inFlightPromise = this.fetchFoods(CATALOG_FOOD_QUERY)
      .then((foods) => {
        this.setSnapshot({
          foods,
          hasLoaded: true,
          isLoading: false,
          error: null,
          source: "supabase",
        });

        return this.snapshot.foods;
      })
      .catch((error) => {
        console.warn("[CatalogFoodStore] Failed to load Supabase catalog foods.", error);

        this.setSnapshot({
          ...this.snapshot,
          hasLoaded: true,
          isLoading: false,
          error,
        });

        return this.snapshot.foods;
      })
      .finally(() => {
        this.inFlightPromise = null;
      });

    return this.inFlightPromise;
  };

  reload = async () => {
    return this.ensureLoaded({ force: true });
  };
}

export const catalogFoodStore = new CatalogFoodStore();

export function getCachedCatalogFoods() {
  return catalogFoodStore.getCachedFoods();
}

export function useCatalogFoods({ autoLoad = true } = {}) {
  const snapshot = useSyncExternalStore(
    catalogFoodStore.subscribe,
    catalogFoodStore.getSnapshot,
    catalogFoodStore.getSnapshot,
  );

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void catalogFoodStore.ensureLoaded();
  }, [autoLoad]);

  return {
    ...snapshot,
    reloadCatalogFoods: catalogFoodStore.reload,
  };
}
